import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList } from '@angular/cdk/drag-drop';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  TemplateRef,
  contentChild,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

/** Fields shared by animation frames and merge images in the list UI. */
export interface ImageListItem {
  id: string;
  name: string;
  previewUrl: string;
  /** Animation frame delay; ignored by merge. */
  durationMs?: number;
  /** Merge custom-layout position; ignored by animation. */
  x?: number;
  y?: number;
}

export interface ImageListPanelLabels {
  titleKey: string;
  expandKey: string;
  collapseKey: string;
  addKey: string;
  emptyKey: string;
  duplicateKey: string;
  deleteKey: string;
}

export interface ImageListReorderEvent {
  id: string;
  position: number;
}

export interface ImageListItemMetaContext<T extends ImageListItem = ImageListItem> {
  $implicit: T;
  index: number;
}

@Component({
  selector: 'lib-collapsible-image-list-panel',
  imports: [CdkDrag, CdkDragHandle, CdkDropList, MatIconModule, MatTooltipModule, NgClass, NgTemplateOutlet, TranslateModule],
  templateUrl: './collapsible-image-list-panel.component.html',
  styleUrl: './collapsible-image-list-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.image-list-panel-collapsed]': 'collapsed()',
  },
})
export class CollapsibleImageListPanelComponent {
  readonly items = input<ImageListItem[]>([]);
  readonly processing = input(false);
  readonly labels = input.required<ImageListPanelLabels>();
  /** When true, the empty-state message is shown (list may still render). */
  readonly showEmptyState = input(false);
  /** When true, rows are focusable and emit `itemSelected` on activate. */
  readonly selectable = input(false);
  /**
   * Label key used for locked rows instead of `item.name`
   * (e.g. animation source frame).
   */
  readonly lockedItemLabelKey = input<string | null>(null);
  /** Lowest index that may be dragged / reordered from (animation locks 0). */
  readonly reorderFromIndex = input(0);
  /** Floor applied to emitted reorder positions (animation uses 1). */
  readonly reorderPositionFloor = input(0);

  readonly canDeleteItem = input<(index: number) => boolean>(() => true);
  readonly canDuplicateItem = input<(index: number) => boolean>(() => true);
  readonly isItemLocked = input<(index: number) => boolean>(() => false);

  readonly filesSelected = output<File[]>();
  readonly itemSelected = output<string>();
  readonly itemReordered = output<ImageListReorderEvent>();
  readonly itemDeleted = output<string>();
  readonly itemDuplicated = output<string>();

  protected readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');
  protected readonly itemMeta = contentChild<TemplateRef<ImageListItemMetaContext>>('itemMeta');

  protected readonly collapsed = signal(false);

  toggleCollapsed(): void {
    this.collapsed.update((value) => !value);
  }

  openFileInput(): void {
    if (this.processing()) {
      return;
    }

    this.fileInput()?.nativeElement.click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';

    if (files.length) {
      this.filesSelected.emit(files);
    }
  }

  trackItem(_index: number, item: ImageListItem): string {
    return item.id;
  }

  canDragItem(index: number): boolean {
    return index >= this.reorderFromIndex() && !this.isItemLocked()(index);
  }

  selectItem(item: ImageListItem): void {
    if (!this.selectable()) {
      return;
    }

    this.itemSelected.emit(item.id);
  }

  onItemKeydown(item: ImageListItem, event: KeyboardEvent): void {
    if (!this.selectable()) {
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.selectItem(item);
  }

  deleteItem(item: ImageListItem, index: number, event: Event): void {
    event.stopPropagation();
    if (this.canDeleteItem()(index)) {
      this.itemDeleted.emit(item.id);
    }
  }

  duplicateItem(item: ImageListItem, index: number, event: Event): void {
    event.stopPropagation();
    if (this.canDuplicateItem()(index)) {
      this.itemDuplicated.emit(item.id);
    }
  }

  dropItem(event: CdkDragDrop<ImageListItem[]>): void {
    if (
      event.previousIndex === event.currentIndex ||
      event.previousIndex < this.reorderFromIndex() ||
      event.currentIndex < this.reorderFromIndex()
    ) {
      return;
    }

    const item = this.items()[event.previousIndex];
    if (!item) {
      return;
    }

    this.itemReordered.emit({
      id: item.id,
      position: Math.max(this.reorderPositionFloor(), event.currentIndex),
    });
  }
}
